/**
 * Market making plans, sold through OpenMarkets in-app purchases (`@openmarketsai/app-sdk` store).
 *
 *   Free       1 active quote
 *   Pro        up to 10 active quotes   $10 one-time      product mm_pro               → entitlement mm_pro
 *   Unlimited  no limit                 $25 one-time      product mm_unlimited         → entitlement mm_unlimited
 *                                       $15 from Pro      product mm_pro_to_unlimited  → entitlement mm_unlimited
 *
 * The store has no upgrade credit, so "Pro → Unlimited" is its own product priced at the
 * difference; this module only ever offers the products that make sense for what you hold.
 * The products are defined in the publisher console; prices shown come from the console when the
 * product list has loaded (the defaults below are only the fallback).
 *
 * "Active" = a quote that isn't closed (working, exposed or balanced), Real and Paper alike,
 * counted over the Market making list: the opportunities filed under greenbook's OpenMarkets
 * model (any device) merged with this browser's legacy registry entries (lib/marketMaking.ts
 * `mergeQuotes`). The cap is still enforced by the app, not the server.
 */

export type MmTier = "free" | "pro" | "unlimited";

export const MM_ENTITLEMENT = { pro: "mm_pro", unlimited: "mm_unlimited" } as const;
export const MM_PRODUCT = { pro: "mm_pro", unlimited: "mm_unlimited", proToUnlimited: "mm_pro_to_unlimited" } as const;

export const TIER_LIMIT: Record<MmTier, number> = { free: 1, pro: 10, unlimited: Infinity };
export const TIER_NAME: Record<MmTier, string> = { free: "Free", pro: "Pro", unlimited: "Unlimited" };

/** The highest tier the held entitlements give. */
export function tierOf(has: (code: string) => boolean): MmTier {
  if (has(MM_ENTITLEMENT.unlimited)) return "unlimited";
  if (has(MM_ENTITLEMENT.pro)) return "pro";
  return "free";
}

/** May another quote be started with `active` already running? */
export function canAddQuote(tier: MmTier, active: number): boolean {
  return active < TIER_LIMIT[tier];
}

export interface MmOffer {
  code: string;
  /** The tier it takes you to. */
  tier: MmTier;
  priceCents: number;
  /** "Up to 10 quotes at once". */
  blurb: string;
  /** "$15 because you have Pro". */
  note: string | null;
}

/** What a console product looks like here (StoreProduct, narrowed). */
export interface ProductLike {
  code: string;
  price_cents: number;
  owned?: boolean;
}

const DEFAULT_PRICE: Record<string, number> = {
  [MM_PRODUCT.pro]: 1000,
  [MM_PRODUCT.unlimited]: 2500,
  [MM_PRODUCT.proToUnlimited]: 1500,
};

/**
 * The upgrades to offer from `tier`. With the console's product list, only products it has (and
 * at its prices); without it (still loading, or it failed) the defaults, so the panel can render.
 */
export function offersFor(tier: MmTier, products: readonly ProductLike[] | null): MmOffer[] {
  const pick = (code: string): number | null => {
    if (!products) return DEFAULT_PRICE[code] ?? null;
    const p = products.find((x) => x.code === code && !x.owned);
    return p ? p.price_cents : null;
  };
  const out: MmOffer[] = [];
  const add = (code: string, to: MmTier, blurb: string, note: string | null) => {
    const price = pick(code);
    if (price != null) out.push({ code, tier: to, priceCents: price, blurb, note });
  };
  if (tier === "free") {
    add(MM_PRODUCT.pro, "pro", "Up to 10 quotes at once", null);
    add(MM_PRODUCT.unlimited, "unlimited", "No limit on quotes", null);
  } else if (tier === "pro") {
    add(MM_PRODUCT.proToUnlimited, "unlimited", "No limit on quotes", "Upgrade price: you already have Pro");
  }
  return out;
}

export function fmtCents(cents: number): string {
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
}
